"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Download, Eye, FileText, Plus, Search } from "lucide-react";
import { format } from "date-fns";
import { useDemoData } from "@/hooks/useDemoData";
import { useQuery } from "@tanstack/react-query";
import { getInvoices, Invoice } from "@/api/receiver/getInvoices";
import { getLoadsForInvoice } from "@/api/receiver/getLoadsForInvoice";

export default function ReceiverInvoicing() {
  const [selectedLoads, setSelectedLoads] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { data: invoicesUnsorted } = useQuery({
    queryKey: ["receiver", "invoices"],
    queryFn: () => getInvoices(),
  });

  const { data: loads } = useQuery({
    queryKey: ["receiver", "loads"],
    queryFn: () =>
      selectedInvoice?.id
        ? getLoadsForInvoice({ invoiceId: selectedInvoice.id })
        : Promise.resolve([]),
    enabled: !!selectedInvoice,
  });

  function highlightMatch(text: string, query: string) {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  }

  const handleLoadSelection = (loadId: string, checked: boolean) => {
    if (checked) {
      setSelectedLoads([...selectedLoads, loadId]);
    } else {
      setSelectedLoads(selectedLoads.filter((id) => id !== loadId));
    }
  };

  const generatePDF = (invoice: any) => {
    // Placeholder for PDF generation logic
    console.log("Generating PDF for invoice:", invoice);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      Paid: "bg-green-500",
      Pending: "bg-lupurple",
      Overdue: "bg-orange-300",
    } as const;

    return (
      <Badge
        className={variants[status as keyof typeof variants] || "bg-slate-400"}
      >
        {status}
      </Badge>
    );
  };

  const invoices = invoicesUnsorted?.receivedInvoices.sort((a, b) => {
    return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
  });

  return (
    <div className=" mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Accounts Payable</h1>
          <p className="text-muted-foreground">
            Manage your invoices and billing
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>Manage and track your invoices</CardDescription>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>

            {invoices && (
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {highlightMatch(invoice.name, searchTerm)}
                    </TableCell>
                    <TableCell>
                      {highlightMatch("Transfire", searchTerm)}
                    </TableCell>
                    <TableCell>
                      {format(invoice.createdAt, "MM-dd-yy")}
                    </TableCell>
                    <TableCell>{format(invoice.dueDate, "MM-dd-yy")}</TableCell>
                    <TableCell>
                      ${(invoice.amountCents / 100).toFixed(2)}
                    </TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => generatePDF(invoice)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </CardContent>
      </Card>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details - {selectedInvoice?.name}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer</Label>
                  <div className="font-light">{"Transfire"}</div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="font-thin">
                    {getStatusBadge(selectedInvoice.status)}
                  </div>
                </div>
                <div>
                  <Label>Invoice Date</Label>
                  <div className="font-light">
                    {format(selectedInvoice.createdAt, "MM-dd-yy")}
                  </div>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <div className="font-light">
                    {format(selectedInvoice.dueDate, "MM-dd-yy")}
                  </div>
                </div>
              </div>

              <div>
                <Label>Loads Included</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Load Name</TableHead>
                      <TableHead>Carrier Info</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loads?.map((load) => (
                      <TableRow key={load.id}>
                        <TableCell>{load.name}</TableCell>
                        <TableCell>{`(${load.carrierName}) ${load.driverName}`}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {load.pickupLocation} → {load.deliveryLocation}
                          </div>
                        </TableCell>
                        <TableCell>
                          ${(load.chargeReceiver / 100).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total Amount:</span>
                  <span>${(selectedInvoice.amountCents / 100).toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => generatePDF(selectedInvoice)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
